import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';
import { connect } from 'react-redux';

import { getAnimationsList } from 'actions/solutionsActions';
import { renderSelect as RenderSelect } from 'components/form-components';

const SHARED_VIDEO_KEYS = ['allLocales', '__all', '_all', 'all', 'shared'];

const normalizeVideoFilename = (value) => (
  typeof value === 'string' ? value.trim() : ''
);

const getSharedVideoFilename = (value) => {
  if (typeof value === 'string') return normalizeVideoFilename(value);
  if (!value || typeof value !== 'object') return '';

  for (let index = 0; index < SHARED_VIDEO_KEYS.length; index += 1) {
    const filename = normalizeVideoFilename(value[SHARED_VIDEO_KEYS[index]]);
    if (filename) return filename;
  }

  const uniqueLegacyFilenames = Array.from(new Set(
    Object.keys(value)
      .filter((key) => SHARED_VIDEO_KEYS.indexOf(key) === -1)
      .map((key) => normalizeVideoFilename(value[key]))
      .filter(Boolean)
  ));

  return uniqueLegacyFilenames.length === 1 ? uniqueLegacyFilenames[0] : '';
};

const hasMultipleLegacyValues = (value) => {
  if (!value || typeof value !== 'object') return false;

  const uniqueLegacyFilenames = Array.from(new Set(
    Object.keys(value)
      .filter((key) => SHARED_VIDEO_KEYS.indexOf(key) === -1)
      .map((key) => normalizeVideoFilename(value[key]))
      .filter(Boolean)
  ));

  return uniqueLegacyFilenames.length > 1;
};

const renderSharedParentVideoSelect = ({ input, options, meta }) => {
  const selectedFilename = getSharedVideoFilename(input.value);
  const hasLegacyConflict = !selectedFilename && hasMultipleLegacyValues(input.value);
  const originalValue = input.value;

  const sharedInput = {
    ...input,
    value: selectedFilename,
    onChange: (filename) => {
      input.onChange(filename ? { allLocales: filename } : {});
    },
    onBlur: () => input.onBlur(originalValue),
  };

  return (
    <RenderSelect
      input={sharedInput}
      meta={meta}
      options={options}
      isClearable
      marginBottom="0"
      placeholder={
        hasLegacyConflict
          ? 'Multiple existing language-specific files - select one shared filename'
          : 'Select...'
      }
    />
  );
};

renderSharedParentVideoSelect.propTypes = {
  input: PropTypes.object.isRequired,
  options: PropTypes.array.isRequired,
  meta: PropTypes.object,
};

const ParentVideosControls = ({
  locales,
  getAnimationsList,
  name = 'parentVideos',
  title = 'Parent Videos:',
  sharedAcrossLocales = false,
}) => {
  const [parentVideos, setParentVideos] = useState(null);

  useEffect(() => {
    const getList = async () => {
      const result = await getAnimationsList(['ParentVideos']);
      setParentVideos(result.ParentVideos);
    };

    getList();
  }, [getAnimationsList]);

  const sharedOptions = useMemo(() => {
    if (!parentVideos) return [];

    const filenames = [];
    Object.keys(parentVideos).forEach((localeName) => {
      (parentVideos[localeName] || []).forEach((item) => {
        const filename = normalizeVideoFilename(item && item.filename);
        if (filename) filenames.push(filename);
      });
    });

    return Array.from(new Set(filenames))
      .sort((a, b) => a.localeCompare(b))
      .map((filename) => ({ label: filename, value: filename }));
  }, [parentVideos]);

  return (
    <div>
      <div style={{ margin: '2rem 0' }}>
        <b>{title}</b>
      </div>

      {sharedAcrossLocales && parentVideos && (
        <div style={{ width: 320, marginBottom: 15 }}>
          <Field
            name={name}
            component={renderSharedParentVideoSelect}
            options={sharedOptions}
          />
          <small style={{ display: 'block', marginTop: 6 }}>
            The selected filename is reused for every language.
          </small>
        </div>
      )}

      {!sharedAcrossLocales && parentVideos &&
        locales.map((loc) => {
          const byLocale = parentVideos[loc.name] || [];

          const parentVideosOptions = byLocale.map((a) => ({
            label: a.filename,
            value: a.filename,
          }));

          return (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: 15,
              }}
              key={loc._id}
            >
              <div style={{ width: '80px', marginRight: '2rem' }}>
                <p style={{ textAlign: 'right', margin: 0 }}>
                  {loc.name}
                </p>
              </div>

              <div style={{ width: '200px' }}>
                <Field
                  name={`${name}[${loc.code}]`}
                  label="Parent Video"
                  component={RenderSelect}
                  isClearable
                  options={parentVideosOptions}
                />
              </div>
            </div>
          );
        })}
    </div>
  );
};

ParentVideosControls.propTypes = {
  getAnimationsList: PropTypes.func.isRequired,
  locales: PropTypes.array.isRequired,
  name: PropTypes.string,
  title: PropTypes.string,
  sharedAcrossLocales: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  locales: state.common.locales,
});

export default connect(mapStateToProps, { getAnimationsList })(
  ParentVideosControls
);
